// `.bbreplay` format (REP-2). A replay is input-only: `{seed, options}` to rebuild
// the world, a per-tick `InputFrame` stream (the movement/look/edits/commands the
// session consumed), and periodic world-hash `keyframes` for seeking + drift
// detection (REP-1). It is kilobyte-scale — it stores *inputs*, never chunks — and
// re-simulates the pure §2 sim (see `player.ts`) to reproduce the exact final state.
//
// This module is the byte layer + schema: a hand binary codec (compact `.bbreplay`)
// and a JSON-lines export (ENG-11), which are inverses — the round-trip test folds
// a random replay through both and asserts deep equality. It is PURE sim (no DOM,
// no netproto import — the button bitfield is defined here so `sim/replay/**` stays
// self-contained; the values mirror the wire `Btn` so a future bridge is trivial).
import { z } from 'zod';
import { ByteWriter, ByteReader } from './bytes';
import { REP } from '../config';
import type { MoveInput } from '../engine/physics';

// ── Input contract ───────────────────────────────────────────────────────────
/** Movement/action button bitfield in an `InputFrame` (mirrors the wire `Btn`). */
export const RepBtn = {
  Forward: 1 << 0,
  Back: 1 << 1,
  Left: 1 << 2,
  Right: 1 << 3,
  Jump: 1 << 4,
  Sneak: 1 << 5,
  Sprint: 1 << 6,
} as const;

/** Per-tick movement-mode flags (creative flight / spectator no-clip). */
export const RepFlag = {
  Flying: 1 << 0,
  NoClip: 1 << 1,
} as const;

/** Map a frame's button bitfield to the physics `MoveInput` (same mapping the
 *  net session uses, kept here so replay never imports the transport layer). */
export function buttonsToMoveInput(b: number): MoveInput {
  return {
    forward: (b & RepBtn.Forward) !== 0,
    back: (b & RepBtn.Back) !== 0,
    left: (b & RepBtn.Left) !== 0,
    right: (b & RepBtn.Right) !== 0,
    jump: (b & RepBtn.Jump) !== 0,
    sneak: (b & RepBtn.Sneak) !== 0,
    sprint: (b & RepBtn.Sprint) !== 0,
  };
}

// ── Types ────────────────────────────────────────────────────────────────────
/** A block edit applied on a tick: the resolved global state written at (x,y,z)
 *  (a break stores the air state). `place` is kept for the UI/timeline only. */
export interface EditOp {
  x: number;
  y: number;
  z: number;
  state: number;
  place: boolean;
}

/** One tick of consumed input. Most ticks carry only look + buttons (a byte or
 *  two on the wire); `edits`/`commands` are usually empty. */
export interface InputFrame {
  buttons: number;
  yaw: number;
  pitch: number;
  flags: number;
  edits: EditOp[];
  commands: string[];
}

/** World-construction options that, with the seed, rebuild the exact world. */
export interface ReplayOptions {
  flat: boolean;
  flatPreset: string; // '' = none
  dimension: 'overworld' | 'nether' | 'end';
}

/** A seek anchor: the `hashWorld` digest the re-sim must reproduce at this tick. */
export interface Keyframe {
  tick: number;
  hash: string;
}

/** One edited section captured in a start snapshot (matches `getDirtySections`). */
export interface SnapshotSection {
  cx: number;
  cz: number;
  sy: number;
  states: number[] | null;
}

/** Captured world+player state to start a replay from mid-world (REP-1): used when
 *  the recording begins after tick 0 or from a world that cannot be re-derived from
 *  {seed, options} alone (e.g. an Atlas voxelization).
 *
 *  `loadedChunks` is the coordinate list of every chunk that was resident — NOT their
 *  block data. `hashWorld` folds all loaded chunks, so a snapshot restart must reload
 *  the same set to reproduce the digest; unedited chunks regenerate identically from
 *  the seed, so only the edited `sections` carry real data. That keeps a snapshot
 *  kilobyte-scale while still being hash-exact. */
export interface Snapshot {
  tickCount: number;
  dayTime: number;
  weather: { raining: boolean; thundering: boolean; rainCountdown: number; thunderCountdown: number };
  player: { x: number; y: number; z: number; vx: number; vy: number; vz: number; yaw: number; pitch: number };
  loadedChunks: Array<{ cx: number; cz: number }>;
  sections: SnapshotSection[];
}

/** A complete recording. `startSnapshot` present ⇒ start from it; else from a fresh
 *  `{seed, options}` world with the player at `spawn`. */
export interface Replay {
  version: number;
  seed: string;
  options: ReplayOptions;
  startTick: number;
  spawn: { x: number; y: number; z: number; yaw: number; pitch: number };
  frames: InputFrame[];
  keyframes: Keyframe[];
  startSnapshot?: Snapshot;
}

// ── Schemas (structural contract) ────────────────────────────────────────────
export const zEditOp = z.object({
  x: z.number().int(), y: z.number().int(), z: z.number().int(),
  state: z.number().int().min(0), place: z.boolean(),
});
export const zInputFrame = z.object({
  buttons: z.number().int().min(0), yaw: z.number(), pitch: z.number(),
  flags: z.number().int().min(0), edits: z.array(zEditOp), commands: z.array(z.string()),
});
export const zReplayOptions = z.object({
  flat: z.boolean(), flatPreset: z.string(), dimension: z.enum(['overworld', 'nether']),
});
export const zKeyframe = z.object({ tick: z.number().int().min(0), hash: z.string() });
export const zSnapshotSection = z.object({
  cx: z.number().int(), cz: z.number().int(), sy: z.number().int().min(0).max(23),
  states: z.array(z.number().int().min(0)).nullable(),
});
export const zSnapshot = z.object({
  tickCount: z.number().int().min(0), dayTime: z.number().int().min(0),
  weather: z.object({
    raining: z.boolean(), thundering: z.boolean(),
    rainCountdown: z.number().int(), thunderCountdown: z.number().int(),
  }),
  player: z.object({
    x: z.number(), y: z.number(), z: z.number(),
    vx: z.number(), vy: z.number(), vz: z.number(), yaw: z.number(), pitch: z.number(),
  }),
  loadedChunks: z.array(z.object({ cx: z.number().int(), cz: z.number().int() })),
  sections: z.array(zSnapshotSection),
});
export const zReplay = z.object({
  version: z.number().int().min(1), seed: z.string(), options: zReplayOptions,
  startTick: z.number().int().min(0),
  spawn: z.object({ x: z.number(), y: z.number(), z: z.number(), yaw: z.number(), pitch: z.number() }),
  frames: z.array(zInputFrame), keyframes: z.array(zKeyframe),
  startSnapshot: zSnapshot.optional(),
});

/** Validate a decoded/parsed replay and reject a newer/garbage struct with a clear
 *  message rather than crashing (E-33). */
export function parseReplay(value: unknown): Replay {
  const r = zReplay.parse(value);
  if (r.version > REP.FORMAT_VERSION) {
    throw new Error(`unsupported .bbreplay version ${r.version} (this build reads up to ${REP.FORMAT_VERSION})`);
  }
  return r;
}

// ── Binary codec (`.bbreplay`) ───────────────────────────────────────────────
function writeFrame(w: ByteWriter, f: InputFrame): void {
  w.varint(f.buttons).f64(f.yaw).f64(f.pitch).varint(f.flags);
  w.varint(f.edits.length);
  for (const e of f.edits) w.svarint(e.x).svarint(e.y).svarint(e.z).varint(e.state).bool(e.place);
  w.varint(f.commands.length);
  for (const c of f.commands) w.str(c);
}
function readFrame(r: ByteReader): InputFrame {
  const buttons = r.varint(), yaw = r.f64(), pitch = r.f64(), flags = r.varint();
  const ne = r.varint();
  const edits: EditOp[] = [];
  for (let i = 0; i < ne; i++) edits.push({ x: r.svarint(), y: r.svarint(), z: r.svarint(), state: r.varint(), place: r.bool() });
  const nc = r.varint();
  const commands: string[] = [];
  for (let i = 0; i < nc; i++) commands.push(r.str());
  return { buttons, yaw, pitch, flags, edits, commands };
}

function writeSnapshot(w: ByteWriter, s: Snapshot): void {
  w.varint(s.tickCount).varint(s.dayTime);
  w.bool(s.weather.raining).bool(s.weather.thundering).svarint(s.weather.rainCountdown).svarint(s.weather.thunderCountdown);
  const p = s.player;
  w.f64(p.x).f64(p.y).f64(p.z).f64(p.vx).f64(p.vy).f64(p.vz).f64(p.yaw).f64(p.pitch);
  w.varint(s.loadedChunks.length);
  for (const c of s.loadedChunks) w.svarint(c.cx).svarint(c.cz);
  w.varint(s.sections.length);
  for (const sec of s.sections) {
    w.svarint(sec.cx).svarint(sec.cz).u8(sec.sy);
    if (sec.states === null) w.bool(false);
    else { w.bool(true).u16array(sec.states); }
  }
}
function readSnapshot(r: ByteReader): Snapshot {
  const tickCount = r.varint(), dayTime = r.varint();
  const weather = { raining: r.bool(), thundering: r.bool(), rainCountdown: r.svarint(), thunderCountdown: r.svarint() };
  const player = { x: r.f64(), y: r.f64(), z: r.f64(), vx: r.f64(), vy: r.f64(), vz: r.f64(), yaw: r.f64(), pitch: r.f64() };
  const nc = r.varint();
  const loadedChunks: Array<{ cx: number; cz: number }> = [];
  for (let i = 0; i < nc; i++) loadedChunks.push({ cx: r.svarint(), cz: r.svarint() });
  const n = r.varint();
  const sections: SnapshotSection[] = [];
  for (let i = 0; i < n; i++) {
    const cx = r.svarint(), cz = r.svarint(), sy = r.u8();
    const present = r.bool();
    sections.push({ cx, cz, sy, states: present ? r.u16array() : null });
  }
  return { tickCount, dayTime, weather, player, loadedChunks, sections };
}

/** Serialize a replay to compact `.bbreplay` bytes. */
export function encodeReplay(rep: Replay): Uint8Array {
  const w = new ByteWriter(256);
  w.varint(rep.version).str(rep.seed);
  w.bool(rep.options.flat).str(rep.options.flatPreset).str(rep.options.dimension);
  w.varint(rep.startTick);
  w.f64(rep.spawn.x).f64(rep.spawn.y).f64(rep.spawn.z).f64(rep.spawn.yaw).f64(rep.spawn.pitch);
  w.varint(rep.frames.length);
  for (const f of rep.frames) writeFrame(w, f);
  w.varint(rep.keyframes.length);
  for (const k of rep.keyframes) w.varint(k.tick).str(k.hash);
  if (rep.startSnapshot) { w.bool(true); writeSnapshot(w, rep.startSnapshot); }
  else w.bool(false);
  return w.finish();
}

/** Parse `.bbreplay` bytes back into a validated replay (E-33 on a newer version). */
export function decodeReplay(bytes: Uint8Array): Replay {
  const r = new ByteReader(bytes);
  const version = r.varint();
  const seed = r.str();
  const options: ReplayOptions = { flat: r.bool(), flatPreset: r.str(), dimension: r.str() as ReplayOptions['dimension'] };
  const startTick = r.varint();
  const spawn = { x: r.f64(), y: r.f64(), z: r.f64(), yaw: r.f64(), pitch: r.f64() };
  const nf = r.varint();
  const frames: InputFrame[] = [];
  for (let i = 0; i < nf; i++) frames.push(readFrame(r));
  const nk = r.varint();
  const keyframes: Keyframe[] = [];
  for (let i = 0; i < nk; i++) keyframes.push({ tick: r.varint(), hash: r.str() });
  const rep: Replay = { version, seed, options, startTick, spawn, frames, keyframes };
  if (r.bool()) rep.startSnapshot = readSnapshot(r);
  return parseReplay(rep);
}

// ── JSON-lines export (ENG-11) ───────────────────────────────────────────────
// Line 0 = header (everything but frames); each following line = one InputFrame.
// Human-diffable and streamable; `fromJsonl`/`toJsonl` are inverses.
/** Export a replay as JSON-lines text (header line + one line per frame). */
export function replayToJsonl(rep: Replay): string {
  const header = {
    version: rep.version, seed: rep.seed, options: rep.options, startTick: rep.startTick,
    spawn: rep.spawn, keyframes: rep.keyframes,
    ...(rep.startSnapshot ? { startSnapshot: rep.startSnapshot } : {}),
  };
  const lines = [JSON.stringify(header), ...rep.frames.map((f) => JSON.stringify(f))];
  return lines.join('\n');
}

/** Parse JSON-lines text back into a validated replay. */
export function replayFromJsonl(text: string): Replay {
  const lines = text.split('\n').filter((l) => l.trim() !== '');
  if (lines.length === 0) throw new Error('empty .bbreplay JSONL');
  const header = JSON.parse(lines[0]!) as Record<string, unknown>;
  const frames = lines.slice(1).map((l) => JSON.parse(l));
  return parseReplay({ ...header, frames });
}
